import { useState, useEffect, type ChangeEvent } from 'react';
import { useContacts } from '../hooks/useContacts';
import { ContactList } from '../components/ContactList';
import { ContactForm } from '../components/ContactForm';
import { CategoryModal } from '../components/CategoryModal';
import { CSVImportModal } from '../components/CSVImportModal';
import { SearchAndFilters } from '../components/SearchAndFilters';
import { Pagination } from '../components/Pagination';
import { Header } from '../components/Header';
import { Contact } from '../types';
import { useAuth } from '../contexts/AuthContext';
import { usePageActions } from '../contexts/PageActionsContext';
import { apiService } from '../services/api';
import toast from 'react-hot-toast';

const CONTACT_PAGE_SIZE_OPTIONS = [30, 100, 200, 400] as const;
type ContactPageSize = typeof CONTACT_PAGE_SIZE_OPTIONS[number];
const DEFAULT_CONTACT_PAGE_SIZE: ContactPageSize = 30;

export function ContactsPage() {
  const [search, setSearch] = useState('');
  const [selectedCategoryId, setSelectedCategoryId] = useState<string | undefined>();
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState<ContactPageSize>(DEFAULT_CONTACT_PAGE_SIZE);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingContact, setEditingContact] = useState<Contact | undefined>();
  const [isCategoryModalOpen, setIsCategoryModalOpen] = useState(false);
  const [isCSVImportModalOpen, setIsCSVImportModalOpen] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  const { isUrlLogin } = useAuth();
  const { registerActions, clearActions, registerMetadata } = usePageActions();

  const { contacts, total, totalPages, loading, error, refresh, deleteContact, deleteContacts } = useContacts({
    search: search || undefined,
    categoryId: selectedCategoryId,
    page: currentPage,
    pageSize,
  });

  const handleSearchChange = (newSearch: string) => {
    setSearch(newSearch);
    setCurrentPage(1);
    setSelectedIds(new Set());
  };

  const handleCategoryChange = (categoryId: string | undefined) => {
    setSelectedCategoryId(categoryId);
    setCurrentPage(1);
    setSelectedIds(new Set());
  };

  const handleClearFilters = () => {
    setSearch('');
    setSelectedCategoryId(undefined);
    setCurrentPage(1);
    setSelectedIds(new Set());
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    setSelectedIds(new Set());
  };

  const handlePageSizeChange = (event: ChangeEvent<HTMLSelectElement>) => {
    const nextPageSize = Number(event.target.value) as ContactPageSize;
    if (!CONTACT_PAGE_SIZE_OPTIONS.includes(nextPageSize)) return;

    setPageSize(nextPageSize);
    setCurrentPage(1);
    setSelectedIds(new Set());
  };

  const handleEdit = (contact: Contact) => {
    setEditingContact(contact);
    setIsFormOpen(true);
  };

  const handleFormSuccess = () => {
    setIsFormOpen(false);
    setEditingContact(undefined);
    refresh();
  };

  const handleFormCancel = () => {
    setIsFormOpen(false);
    setEditingContact(undefined);
  };

  const handleNewContact = () => {
    setEditingContact(undefined);
    setIsFormOpen(true);
  };

  const handleOpenCategoryModal = () => {
    setIsCategoryModalOpen(true);
  };

  const handleCloseCategoryModal = () => {
    setIsCategoryModalOpen(false);
  };

  const handleOpenCSVImportModal = () => {
    setIsCSVImportModalOpen(true);
  };

  const handleCloseCSVImportModal = () => {
    setIsCSVImportModalOpen(false);
  };

  const handleCSVImportSuccess = () => {
    refresh();
    setIsCSVImportModalOpen(false);
  };

  const handleExportCSV = async () => {
    try {
      setIsExporting(true);
      await apiService.exportCSV(selectedCategoryId);
      toast.success('Contatos exportados com sucesso!');
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Erro ao exportar contatos';
      toast.error(errorMessage);
    } finally {
      setIsExporting(false);
    }
  };

  const handleBulkDelete = async (ids: string[]) => {
    const success = await deleteContacts(ids);
    if (success) {
      setSelectedIds(new Set());
    }
  };

  // Create actions JSX
  const contactsActions = (
          <div className="flex gap-3">
            <button
              onClick={handleOpenCategoryModal}
              className="px-4 py-2 text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-500 text-sm font-medium transition-colors"
              style={{
                backgroundColor: '#3b3f44'
              }}
              onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#4a4f54')}
              onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = '#3b3f44')}
              aria-label="Gerenciar categorias"
            >
              Categorias
            </button>
            <button
              onClick={handleOpenCSVImportModal}
              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 text-sm font-medium transition-colors"
              aria-label="Importar contatos via CSV"
            >
              Importar CSV
            </button>
            <button
              onClick={handleExportCSV}
              disabled={isExporting}
              className="px-4 py-2 text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm font-medium transition-colors disabled:opacity-50"
              style={{
                backgroundColor: '#2563eb'
              }}
              onMouseEnter={(e) => !isExporting && (e.currentTarget.style.backgroundColor = '#1d4ed8')}
              onMouseLeave={(e) => !isExporting && (e.currentTarget.style.backgroundColor = '#2563eb')}
              aria-label="Exportar contatos em CSV"
            >
              {isExporting ? 'Exportando...' : 'Exportar CSV'}
            </button>
            <button
              onClick={handleNewContact}
              className="btn-primary text-sm font-medium"
              aria-label="Criar novo contato"
            >
              + Novo Contato
            </button>
          </div>
  );

  // Register actions and metadata for iframe mode
  useEffect(() => {
    if (isUrlLogin) {
      registerActions('/contatos', contactsActions);
      registerMetadata('/contatos', {
        title: 'Contatos',
        subtitle: `${total} contatos cadastrados`
      });
    }
    return () => clearActions('/contatos');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isUrlLogin, isCategoryModalOpen, isCSVImportModalOpen, isFormOpen, total]);

  if (error) {
    return (
      <div className="flex items-center justify-center h-96">
          <div className="text-center">
          <h1 className="text-3xl font-bold text-red-600 mb-4">Erro</h1>
          <p className="mb-4 text-sm font-normal" style={{ color: 'var(--text-secondary)' }}>{error}</p>
          <button
            onClick={refresh}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm font-medium"
          >
            Tentar novamente
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Header - Only show in normal mode */}
      {!isUrlLogin && (
        <Header
          title="Contatos"
          subtitle={`${total} contatos cadastrados`}
          actions={contactsActions}
          tooltipId="contacts_index"
        />
      )}

      <div className="p-6 space-y-6">

        <SearchAndFilters
          search={search}
          selectedCategoryId={selectedCategoryId}
          onSearchChange={handleSearchChange}
          onCategoryChange={handleCategoryChange}
          onClearFilters={handleClearFilters}
        />

        <div className="transition-colors">
          <ContactList
            contacts={contacts}
            loading={loading}
            onEdit={handleEdit}
            onDelete={deleteContact}
            selectedIds={selectedIds}
            onSelectionChange={setSelectedIds}
            onBulkDelete={handleBulkDelete}
          />
        </div>

        <div className="mt-6 flex flex-col items-center gap-3">
          {totalPages > 1 && (
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={handlePageChange}
            />
          )}

          <div className="flex flex-col items-center justify-center gap-3 sm:flex-row">
            <div className="text-center text-sm font-normal" style={{ color: 'var(--text-muted)' }}>
              Mostrando {contacts.length} de {total} contatos
            </div>

            <label
              htmlFor="contacts-page-size"
              className="flex items-center gap-2 text-sm font-normal"
              style={{ color: 'var(--text-muted)' }}
            >
              <span>Contatos por página</span>
              <select
                id="contacts-page-size"
                value={pageSize}
                onChange={handlePageSizeChange}
                className="px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors text-sm font-normal"
                style={{
                  backgroundColor: 'var(--bg-card)',
                  borderColor: 'var(--border-color)',
                  color: 'var(--text-primary)'
                }}
                aria-label="Quantidade de contatos por página"
              >
                {CONTACT_PAGE_SIZE_OPTIONS.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
            </label>
          </div>
        </div>
      </div>

      {isFormOpen && (
        <ContactForm
          contact={editingContact}
          onSuccess={handleFormSuccess}
          onCancel={handleFormCancel}
        />
      )}

      <CategoryModal
        isOpen={isCategoryModalOpen}
        onClose={handleCloseCategoryModal}
      />

      <CSVImportModal
        isOpen={isCSVImportModalOpen}
        onClose={handleCloseCSVImportModal}
        onSuccess={handleCSVImportSuccess}
      />
    </>
  );
}
